import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { AlertTriangle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtNumber } from "@/lib/format";

const REACH_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

/**
 * Shared curve chart used by Curve Library + Dashboard.
 *
 * Props:
 *   detail            { data: [{ media_type, input_value, input_metric,
 *                                reach_N_plus... }, ...],
 *                       validation_warnings?: string[] }
 *   showWarnings      default true
 *   showTable         default true (set false on Dashboard to keep compact)
 *   chartHeight       default 256 (h-64). Use 220 for tighter cards.
 */
export default function CurveChart({
  detail,
  showWarnings = true,
  showTable = true,
  chartHeight = 256,
}) {
  if (!detail || !detail.data) {
    return <div className="text-sm text-slate-500 py-8">No data.</div>;
  }
  const tvData = detail.data.filter((d) => d.media_type === "TV");
  const olvData = detail.data.filter((d) => d.media_type === "OLV");
  const tvLevels = tvData[0] ? REACH_KEYS.filter((l) => tvData[0][`reach_${l}_plus`] != null) : [];
  const olvLevels = olvData[0] ? REACH_KEYS.filter((l) => olvData[0][`reach_${l}_plus`] != null) : [];

  return (
    <div className="space-y-4">
      {showWarnings && detail.validation_warnings?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1">
          {detail.validation_warnings.map((w) => (
            <div key={w} className="flex items-start gap-2 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {w}
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue={tvData.length > 0 ? "tv" : "olv"}>
        <TabsList>
          <TabsTrigger value="tv" data-testid="curve-tab-tv" disabled={tvData.length === 0}>TV / GRP Curve</TabsTrigger>
          <TabsTrigger value="olv" data-testid="curve-tab-olv" disabled={olvData.length === 0}>OLV / YouTube Curve</TabsTrigger>
        </TabsList>
        <TabsContent value="tv">
          <CurvePreview
            data={tvData}
            levels={tvLevels}
            inputMetric={tvData[0]?.input_metric || "GRP"}
            color="#0A3D91"
            chartHeight={chartHeight}
            showTable={showTable}
          />
        </TabsContent>
        <TabsContent value="olv">
          <CurvePreview
            data={olvData}
            levels={olvLevels}
            inputMetric={olvData[0]?.input_metric || "Impressions"}
            color="#18B7D8"
            chartHeight={chartHeight}
            showTable={showTable}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CurvePreview({ data, levels, inputMetric, color, chartHeight, showTable }) {
  if (!data || data.length === 0) return <div className="text-sm text-slate-500 py-8">No data.</div>;
  const chartData = data.map((d) => ({
    input: d.input_value,
    "1+": d.reach_1_plus,
    "3+": d.reach_3_plus,
    "5+": d.reach_5_plus,
    "10+": d.reach_10_plus,
  }));
  return (
    <div className="space-y-3">
      <div className="card-harmony p-4">
        <div style={{ height: chartHeight }}>
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="input" fontSize={10} stroke="#64748B" />
              <YAxis fontSize={10} stroke="#64748B" tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v) => `${v?.toFixed(2)}%`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {levels.includes(1) && <Line type="monotone" dataKey="1+" stroke={color} strokeWidth={2} dot={false} />}
              {levels.includes(3) && <Line type="monotone" dataKey="3+" stroke="#0E73C9" strokeWidth={2} dot={false} />}
              {levels.includes(5) && <Line type="monotone" dataKey="5+" stroke="#1CC7B3" strokeWidth={2} dot={false} />}
              {levels.includes(10) && <Line type="monotone" dataKey="10+" stroke="#94A3B8" strokeWidth={2} dot={false} />}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {showTable && (
        <div className="overflow-x-auto card-harmony">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left py-2 px-3">{inputMetric}</th>
                {levels.map((l) => <th key={l} className="text-left py-2 px-3">{l}+</th>)}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 15).map((r) => (
                <tr key={r.input_value} className="border-t border-slate-100">
                  <td className="py-2 px-3 font-medium text-slate-900">{fmtNumber(r.input_value)}</td>
                  {levels.map((l) => (
                    <td key={l} className="py-2 px-3 text-slate-600">
                      {r[`reach_${l}_plus`] != null ? `${r[`reach_${l}_plus`].toFixed(2)}%` : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[11px] text-slate-500 p-2 text-center border-t border-slate-100">
            Showing first 15 of {data.length} rows
          </div>
        </div>
      )}
    </div>
  );
}
